import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { FlowConfig, FlowState, FlowStep, FlowNavigationResult } from '../types/flow';
import { FlowLoader } from '../utils/flowLoader';
import { ClientIdentifier } from '../utils/clientIdentifier';

interface FlowContextType {
  flowConfig: FlowConfig;
  flowState: FlowState;
  currentStep: FlowStep | null;
  isLoading: boolean;
  error: string | null;
  nextStep: () => FlowNavigationResult;
  previousStep: () => FlowNavigationResult;
  goToStep: (stepId: string) => FlowNavigationResult;
  setFlowData: (key: string, value: any) => void;
  getFlowData: (key: string) => any;
  completeStep: (stepId: string) => void;
  skipStep: (stepId: string) => void;
  resetFlow: () => void;
}

const FlowContext = createContext<FlowContextType | undefined>(undefined);

interface FlowProviderProps {
  children: ReactNode;
  fallbackClientId?: string;
}

const FLOW_STATE_KEY = 'username-selection-flow-state';

export const FlowProvider: React.FC<FlowProviderProps> = ({ 
  children, 
  fallbackClientId = 'default' 
}) => {
  const [flowConfig, setFlowConfig] = useState<FlowConfig | null>(null);
  const [flowState, setFlowState] = useState<FlowState>(() => {
    const saved = sessionStorage.getItem(FLOW_STATE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {
          currentStepIndex: 0,
          completedSteps: [],
          skippedSteps: [],
          flowData: {}
        };
      }
    }
    return {
      currentStepIndex: 0,
      completedSteps: [],
      skippedSteps: [],
      flowData: {}
    };
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    sessionStorage.setItem(FLOW_STATE_KEY, JSON.stringify(flowState));
  }, [flowState]);

  const loadFlow = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const clientId = ClientIdentifier.getClientId();
      const flowLoader = FlowLoader.getInstance();
      const config = await flowLoader.loadFlow(clientId);
      setFlowConfig(config);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load flow configuration';
      setError(errorMessage);
      console.error('Flow loading error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFlow();
  }, []);

  const getCurrentStep = useCallback((): FlowStep | null => {
    if (!flowConfig) return null;
    return flowConfig.flow.steps[flowState.currentStepIndex] || null;
  }, [flowConfig, flowState.currentStepIndex]);

  const nextStep = useCallback((): FlowNavigationResult => {
    if (!flowConfig) {
      return { success: false, error: 'Flow configuration not loaded' };
    }

    const currentStep = getCurrentStep();
    if (currentStep && !flowState.completedSteps.includes(currentStep.id)) {
      setFlowState(prev => ({
        ...prev,
        completedSteps: [...prev.completedSteps, currentStep.id]
      }));
    }

    const nextIndex = flowState.currentStepIndex + 1;
    if (nextIndex >= flowConfig.flow.steps.length) {
      return { success: false, error: 'Already at last step' };
    }

    setFlowState(prev => ({
      ...prev,
      currentStepIndex: nextIndex
    }));

    return { 
      success: true, 
      nextStepId: flowConfig.flow.steps[nextIndex].id 
    };
  }, [flowConfig, flowState, getCurrentStep]);

  const previousStep = useCallback((): FlowNavigationResult => {
    if (!flowConfig) {
      return { success: false, error: 'Flow configuration not loaded' };
    }

    if (!flowConfig.flow.settings.allowBack) {
      return { success: false, error: 'Going back is not allowed in this flow' };
    }

    const prevIndex = flowState.currentStepIndex - 1;
    if (prevIndex < 0) {
      return { success: false, error: 'Already at first step' };
    }

    setFlowState(prev => ({
      ...prev,
      currentStepIndex: prevIndex
    }));

    return { 
      success: true, 
      nextStepId: flowConfig.flow.steps[prevIndex].id 
    };
  }, [flowConfig, flowState]);

  const goToStep = useCallback((stepId: string): FlowNavigationResult => {
    if (!flowConfig) {
      return { success: false, error: 'Flow configuration not loaded' };
    }

    const stepIndex = flowConfig.flow.steps.findIndex(step => step.id === stepId);
    if (stepIndex === -1) {
      return { success: false, error: `Step "${stepId}" not found in flow` };
    }

    setFlowState(prev => ({
      ...prev,
      currentStepIndex: stepIndex
    }));

    return { success: true, nextStepId: stepId };
  }, [flowConfig]);

  const setFlowData = useCallback((key: string, value: any) => {
    setFlowState(prev => ({
      ...prev,
      flowData: {
        ...prev.flowData,
        [key]: value
      }
    }));
  }, []);

  const getFlowData = useCallback((key: string): any => {
    return flowState.flowData[key];
  }, [flowState.flowData]);

  const completeStep = useCallback((stepId: string) => {
    setFlowState(prev => ({
      ...prev,
      completedSteps: prev.completedSteps.includes(stepId) 
        ? prev.completedSteps 
        : [...prev.completedSteps, stepId]
    }));
  }, []);

  const skipStep = useCallback((stepId: string) => {
    setFlowState(prev => ({
      ...prev,
      skippedSteps: prev.skippedSteps.includes(stepId)
        ? prev.skippedSteps
        : [...prev.skippedSteps, stepId]
    }));
  }, []);

  const resetFlow = useCallback(() => {
    const initialState: FlowState = {
      currentStepIndex: 0,
      completedSteps: [],
      skippedSteps: [],
      flowData: {}
    };
    setFlowState(initialState);
    sessionStorage.removeItem(FLOW_STATE_KEY);
  }, []);

  const value: FlowContextType = {
    flowConfig: flowConfig || {} as FlowConfig,
    flowState,
    currentStep: getCurrentStep(),
    isLoading,
    error,
    nextStep,
    previousStep,
    goToStep,
    setFlowData,
    getFlowData,
    completeStep,
    skipStep,
    resetFlow
  };

  return (
    <FlowContext.Provider value={value}>
      {children}
    </FlowContext.Provider>
  );
};

export const useFlow = (): FlowContextType => {
  const context = useContext(FlowContext);
  if (context === undefined) {
    throw new Error('useFlow must be used within a FlowProvider');
  }
  return context;
};

export const useFlowNavigation = () => {
  const { nextStep, previousStep, goToStep } = useFlow();
  return { nextStep, previousStep, goToStep };
};

export const useCurrentStep = () => {
  const { currentStep, flowState } = useFlow();
  return { currentStep, flowState };
};

export default FlowContext;
